import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { profileMetadataSchema, isWebLinkUrl } from "../types/profile-metadata";

// website_url has no scheme guard in the legacy schema; enforce http(s) so a
// direct mutator call can't store a javascript:/data: URL that later renders
// as an href on the (public) profile card. Empty string clears the field.
const webLinkUrl = z
  .string()
  .refine((u) => u === "" || isWebLinkUrl(u), "Website must be an http(s) URL");

// The profile-display fields whose change should bump profile_updated_at.
const PROFILE_WATERMARK_FIELDS = [
  "display_name",
  "avatar_url",
  "headline",
  "bio",
  "location",
  "timezone",
  "website_url",
  "profile_metadata",
] as const;

export const usersMutators = {
  // Identity-only insert. Hot-mutating fields moved to user_presence in
  // Phase 1.5. The mutator atomically creates both rows so any new user
  // has a presence row from the start (parallels the backfill in
  // PR 1.5.1 that covered existing users).
  insert: defineMutator(
    z.object({
      id: z.string(),
      email: z.string(),
      display_name: z.string(),
      avatar_url: z.string().optional(),
      headline: z.string().optional().nullable(),
      bio: z.string().optional().nullable(),
      location: z.string().optional().nullable(),
      timezone: z.string().optional().nullable(),
      website_url: webLinkUrl.optional().nullable(),
      is_deactivated: z.boolean().optional(),
      is_coworker: z.boolean().optional(),
      // WS-B1: home_region defaults to 'us' until WS-B2 wires the
      // /api/user/bootstrap geo-IP resolution.
      home_region: z.string().optional(),
      created_at: z.number(),
      updated_at: z.number().optional().nullable(),
      deleted_at: z.number().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      // Pattern 1 (userId-match): a user may only create their OWN identity
      // row. Without this, because the write below is an `upsert` keyed on
      // the PK `id`, any authenticated client could overwrite another user's
      // row in place — impersonating them or setting `is_deactivated=true` to
      // lock them out. Mirrors the guard on the sibling `update` mutator.
      if (ctx?.sub && args.id !== ctx.sub) {
        throw new Error("Unauthorized: can only create own profile");
      }
      // Phase 1.5.6 — `is_online` was dropped from the Zero `users`
      // schema in this PR; identity-only fields go here, presence
      // initialization happens below on the user_presence row. The
      // legacy users.is_online column still exists in Postgres until
      // PR 1.5.7 drops it but no longer flows through Zero, so we no
      // longer set it from the client schema.
      await tx.mutate.users.upsert({
        ...args,
        is_deactivated: args.is_deactivated ?? false,
        is_guest: false,
        is_coworker: args.is_coworker ?? false,
        agent_status_opt_in: false,
        // WS-B1: defaults until WS-B2 wires WorkOS bootstrap with geo-IP.
        home_region: args.home_region ?? "us",
        profile_updated_at: args.created_at,
      });
      // Pair: ensure user_presence row exists. Server-only conditional
      // insert (Pattern 2) — `tx.run` on the client only sees cached
      // data, so we'd false-negative and clobber live state. On the
      // server we look up the row first and skip the insert when it
      // already exists (backfilled in PR 1.5.1 or created by a prior
      // sign-in). Don't switch to `upsert`: that would overwrite live
      // is_online / typing_* / terminal_* / status_* with the all-null
      // first-sign-in defaults below.
      if (tx.location === "server") {
        const existing = await tx.run(
          zql.user_presence.where("user_id", args.id).one(),
        );
        if (!existing) {
          await tx.mutate.user_presence.insert({
            user_id: args.id,
            is_online: true,
            last_active_at: Date.now(),
            terminal_share: false,
            typing_in: null,
            typing_at: null,
            status: null,
            status_emoji: null,
            status_expiry: null,
            terminal_process: null,
            terminal_cwd: null,
            terminal_active_at: null,
          });
        }
      }
    },
  ),
  // Identity-only update. Hot-mutating presence/typing/status/terminal
  // fields moved to `mutators.user_presence.*` in Phase 1.5 — see
  // ~/.claude/plans/zero-perf-rollout.md. Old args (status, is_online,
  // typing_*, terminal_*, status_emoji, status_expiry, last_active_at)
  // are deliberately absent so any straggler caller fails Zod parsing
  // AND TypeScript type-checking, surfacing the missed migration.
  update: defineMutator(
    z.object({
      id: z.string(),
      email: z.string().optional(),
      display_name: z.string().optional(),
      avatar_url: z.string().optional().nullable(),
      headline: z.string().optional().nullable(),
      bio: z.string().optional().nullable(),
      location: z.string().optional().nullable(),
      timezone: z.string().optional().nullable(),
      website_url: webLinkUrl.optional().nullable(),
      // Public-community profile extras (social links, "building now", linked
      // GitHub username). Validated against the shared blob shape.
      profile_metadata: profileMetadataSchema.optional().nullable(),
      is_deactivated: z.boolean().optional(),
      agent_status_opt_in: z.boolean().optional(),
      updated_at: z.number().optional().nullable(),
      deleted_at: z.number().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.id !== ctx.sub) {
        throw new Error("Unauthorized: can only update own profile");
      }
      // Bump the cross-region denorm watermark ONLY when a profile-bearing
      // field actually changed — the per-sign-in self-heal (current-user.tsx)
      // calls update with just {id, email, updated_at}, which must NOT churn
      // the watermark on every login.
      const touchedProfile = PROFILE_WATERMARK_FIELDS.some(
        (f) => args[f] !== undefined,
      );
      await tx.mutate.users.update(
        touchedProfile ? { ...args, profile_updated_at: Date.now() } : args,
      );
    },
  ),
};
